import React, { useEffect, useState, useRef, useCallback } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, or, limit } from 'firebase/firestore';
import ProductCard from './ProductCard';
import SectionTitle from './SectionTitle';

const LIMITE_POR_FILA        = 20;
const LIMITE_BUSQUEDA_GLOBAL = 60;

const deduplicarPorId = (docs) => {
  const mapa = new Map();
  docs.forEach((doc) => { if (!mapa.has(doc.id)) mapa.set(doc.id, doc); });
  return Array.from(mapa.values());
};

// Barajar aleatoriamente para rotar productos en cada visita
const barajarArray = (array) => {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const ProductList = ({ categoriaBusqueda, tituloMostrar, busquedaGlobal, addToCart, onVerTodo, vistaAfiliado = false }) => {
  const [productos, setProductos]           = useState([]);
  const [loading, setLoading]               = useState(false);
  const [canScrollLeft, setCanScrollLeft]   = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hayOverflow, setHayOverflow]       = useState(false);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const obtenerProductos = async () => {
      try {
        setLoading(true);
        const productosRef = collection(db, 'catalogo');
        let q;

        if (busquedaGlobal) {
          q = query(productosRef, where('visible', '!=', false), limit(LIMITE_BUSQUEDA_GLOBAL));
        } else {
          if (!categoriaBusqueda) return;
          q = query(
            productosRef,
            or(
              where('categoria', '==', categoriaBusqueda),
              where('categorias', 'array-contains', categoriaBusqueda),
              where('tiposUsuario', 'array-contains', categoriaBusqueda)
            ),
            limit(LIMITE_POR_FILA)
          );
        }

        const snap = await getDocs(q);
        let docs = [];
        snap.forEach((doc) => docs.push({ id: doc.id, ...doc.data() }));
        docs = deduplicarPorId(docs);

        if (busquedaGlobal) {
          const termino = busquedaGlobal.toLowerCase();
          docs = docs.filter(p =>
            (p.nombre      && p.nombre.toLowerCase().includes(termino)) ||
            (p.descripcion && p.descripcion.toLowerCase().includes(termino))
          );
        } else {
          docs = docs.filter(p => p.visible !== false);
          // Rotación inteligente: mezclar orden de productos dentro de la categoría
          docs = barajarArray(docs);
        }

        setProductos(docs);
      } catch (error) {
        console.error('Error obteniendo productos:', error);
      } finally {
        setLoading(false);
      }
    };

    obtenerProductos();
  }, [categoriaBusqueda, busquedaGlobal]);

  const actualizarScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const tieneOverflow = el.scrollWidth > el.clientWidth + 4;
    setHayOverflow(tieneOverflow);
    setCanScrollLeft(el.scrollLeft > 6);
    setCanScrollRight(tieneOverflow && (el.scrollLeft + el.clientWidth < el.scrollWidth - 6));
  }, []);

  useEffect(() => {
    actualizarScroll();
    const el = scrollContainerRef.current;
    if (!el) return;

    el.addEventListener('scroll', actualizarScroll, { passive: true });
    window.addEventListener('resize', actualizarScroll);

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(actualizarScroll) : null;
    if (ro) ro.observe(el);

    const timer = setTimeout(actualizarScroll, 150);

    return () => {
      el.removeEventListener('scroll', actualizarScroll);
      window.removeEventListener('resize', actualizarScroll);
      if (ro) ro.disconnect();
      clearTimeout(timer);
    };
  }, [actualizarScroll, productos]);

  const scrollManual = (direction) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const amount = container.clientWidth * 0.8;
    container.scrollBy({ left: direction === 'next' ? amount : -amount, behavior: 'smooth' });
  };

  if (loading)              return null;
  if (productos.length === 0) {
    return busquedaGlobal
      ? <p style={{ textAlign: 'center', color: 'var(--text-muted)', marginTop: 'var(--space-5)' }}>No se encontraron productos.</p>
      : null;
  }

  return (
    <div style={{ width: '100%', marginBottom: 0, position: 'relative' }}>

      {/* Cabecera de sección estandarizada */}
      {!busquedaGlobal && (
        <SectionTitle
          accion={onVerTodo && (hayOverflow || productos.length > 4) ? 'Ver todo' : undefined}
          onAccion={() => onVerTodo && onVerTodo(categoriaBusqueda)}
        >
          {tituloMostrar || categoriaBusqueda}
        </SectionTitle>
      )}

      {/* Carrusel */}
      <div style={{ position: 'relative' }}>

        {/* Flecha izquierda */}
        {!busquedaGlobal && canScrollLeft && (
          <button
            type="button"
            aria-label="Deslizar hacia la izquierda"
            onClick={() => scrollManual('prev')}
            style={flechaStyle('left')}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-verde-hover)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--color-verde)'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M15 18l-6-6 6-6" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}

        {/* Scroll container */}
        <div
          ref={scrollContainerRef}
          className={busquedaGlobal ? '' : 'scroll-oculto'}
          style={{
            display: busquedaGlobal ? 'grid' : 'flex',
            /* Grid para resultados de búsqueda */
            ...(busquedaGlobal && {
              gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
              gap: 'var(--col-gap)',
            }),
            /* Flex para carrusel horizontal */
            ...(!busquedaGlobal && {
              gap: 'var(--space-3)',
              overflowX: hayOverflow ? 'auto' : 'visible',
              overflowY: 'visible',
              flexWrap: 'nowrap',
              justifyContent: hayOverflow ? 'flex-start' : 'center',
              padding: 'var(--space-2) var(--space-1) var(--space-2)',
              scrollBehavior: 'smooth',
            }),
          }}
        >
          {productos.map((prod, index) => (
            <div
              key={prod.id}
              style={busquedaGlobal ? {
                display: 'flex',
              } : {
                minWidth: 'clamp(132px, 37vw, 176px)',
                maxWidth: 'clamp(132px, 37vw, 176px)',
                flexShrink: 0,
                display: 'flex',
              }}
            >
              <ProductCard producto={prod} addToCart={addToCart} index={index} vistaAfiliado={vistaAfiliado} />
            </div>
          ))}
        </div>

        {/* Flecha derecha */}
        {!busquedaGlobal && canScrollRight && (
          <button
            type="button"
            aria-label="Deslizar hacia la derecha"
            onClick={() => scrollManual('next')}
            style={{ ...flechaStyle('right'), animation: 'pulso-der 2.4s ease-in-out infinite' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-verde-hover)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--color-verde)'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M9 18l6-6-6-6" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>

      <style>{`
        .scroll-oculto::-webkit-scrollbar { display: none; }
        .scroll-oculto { -ms-overflow-style: none; scrollbar-width: none; }

        @keyframes pulso-der {
          0%, 100% {
            box-shadow: 0 2px 8px rgba(74,156,63,0.35), 0 0 0 0 rgba(74,156,63,0.4);
            transform: translateY(-50%) translateX(0);
          }
          15% { transform: translateY(-50%) translateX(3px); }
          30% {
            box-shadow: 0 2px 8px rgba(74,156,63,0.35), 0 0 0 8px rgba(74,156,63,0);
            transform: translateY(-50%) translateX(0);
          }
        }
      `}</style>
    </div>
  );
};

const flechaStyle = (side) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  [side]: '2px',
  zIndex: 2,
  width: '34px',
  height: '34px',
  borderRadius: '50%',
  border: 'none',
  backgroundColor: 'var(--color-verde)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
  transition: 'background-color var(--transition-fast)',
  boxShadow: '0 2px 8px rgba(74,156,63,0.35)',
});

export default ProductList;
